import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy, ExternalLink, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import { EXTRA_MOUNTS, buildExtraMountNode, funnelHasMountPoint } from '@/components/funnels/buildWizardActions';
import { useFunnelsList, useUpdateFunnelWithRouting } from '@/lib/api/funnels';
import { trpc } from '@/lib/api/trpc';

interface ScannedFunnel {
  id: string;
  name: string;
  config: { nodes?: any[] } & Record<string, any>;
  hasPortal: boolean;
  /** Live portal URL, composed server-side when the mount was routed. */
  url: string | null;
  /** True when the portal is only on the staging config — the funnel was never promoted. */
  isStagingOnly: boolean;
}

const PORTAL_MOUNT_POINT = 'portal';

/**
 * The portal node carries the URL the routing layer built for it, so a merchant is shown the
 * address that is actually served rather than a domain and a path glued together here.
 */
function readPortalUrl(config: { nodes?: any[] } | null | undefined): string | null {
  const node = (config?.nodes ?? []).find(
    (n: any) => n.type === 'custom' && n.config?.metadata?.mountPoint === PORTAL_MOUNT_POINT,
  );
  return node?.config?.url ?? null;
}

export const PortalMountCard = ({ storeId }: { storeId: string }) => {
  const { t } = useTranslation('stores');
  const { toast } = useToast();
  const utils = trpc.useUtils();

  const { data: funnelsData, isLoading: isLoadingFunnels, isError: isFunnelsError } = useFunnelsList(storeId);
  const updateFunnelWithRouting = useUpdateFunnelWithRouting();

  const [scan, setScan] = React.useState<{ loading: boolean; funnels: ScannedFunnel[] }>({
    loading: true,
    funnels: [],
  });
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());
  const [isMounting, setIsMounting] = React.useState(false);
  const [copiedUrl, setCopiedUrl] = React.useState<string | null>(null);
  const [scanNonce, setScanNonce] = React.useState(0);

  // useFunnelsList rebuilds its wrapper object every render, so depend on the id list instead.
  const funnelIdsKey = (funnelsData?.funnels ?? []).map((f: any) => f.id).join(',');

  React.useEffect(() => {
    if (!storeId || isLoadingFunnels) return;
    const list = (funnelsData?.funnels ?? []) as any[];
    let cancelled = false;
    setScan({ loading: true, funnels: [] });
    (async () => {
      // The list endpoint omits node config, so each funnel's full row is needed to tell
      // whether it carries a portal mount and where that mount is served.
      const scanned = await Promise.all(
        list.map(async (f: any): Promise<ScannedFunnel | null> => {
          try {
            const full = await utils.funnels.getById.fetch({ funnelId: f.id, storeId });
            const config = (full as any)?.config;
            if (!config) return null;
            const productionUrl = readPortalUrl((full as any)?.productionConfig);
            const stagingUrl = readPortalUrl(config);
            return {
              id: f.id,
              name: f.name,
              config,
              hasPortal: funnelHasMountPoint(config, PORTAL_MOUNT_POINT),
              url: productionUrl ?? stagingUrl,
              isStagingOnly: !productionUrl,
            };
          } catch {
            return null;
          }
        }),
      );
      if (cancelled) return;
      const funnels = scanned.filter((f): f is ScannedFunnel => f !== null);
      setScan({ loading: false, funnels });
      setSelectedIds(new Set(funnels.filter((f) => !f.hasPortal).map((f) => f.id)));
    })();
    return () => {
      cancelled = true;
    };
  }, [storeId, funnelIdsKey, isLoadingFunnels, scanNonce]);

  const missing = scan.funnels.filter((f) => !f.hasPortal);
  const mounted = scan.funnels.filter((f) => f.hasPortal);
  const selectedCount = missing.filter((f) => selectedIds.has(f.id)).length;

  const toggleSelected = (funnelId: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(funnelId);
      else next.delete(funnelId);
      return next;
    });
  };

  const copyUrl = async (url: string) => {
    await navigator.clipboard.writeText(url);
    setCopiedUrl(url);
    window.setTimeout(() => setCopiedUrl(null), 2000);
  };

  // Append a /portal mount step to each selected funnel. The backend fills the node's
  // instanceId and route on save, so no separate deploy is needed.
  const addPortalToSelectedFunnels = async () => {
    const portalMount = EXTRA_MOUNTS.find((m) => m.mountPoint === PORTAL_MOUNT_POINT);
    const targets = missing.filter((f) => selectedIds.has(f.id));
    if (!portalMount || targets.length === 0) return;

    setIsMounting(true);
    try {
      for (const target of targets) {
        const config = { ...target.config, name: target.config.name ?? target.name };
        const nodes = config.nodes ?? [];
        const checkout = nodes.find((n: any) => n.type === 'checkout');
        const portalNode = buildExtraMountNode(
          portalMount,
          checkout?.position?.x ?? 0,
          checkout?.position?.y ?? 0,
        );
        await updateFunnelWithRouting.mutateAsync({
          funnelId: target.id,
          storeId,
          config: { ...config, nodes: [...nodes, portalNode] },
        } as any);
      }
      toast({
        title: t('customerPortal.mount.successTitle'),
        description: t('customerPortal.mount.successDescription'),
      });
      await utils.funnels.getById.invalidate();
      await utils.funnels.list.invalidate();
      setScanNonce((n) => n + 1);
    } catch (error) {
      toast({
        title: t('customerPortal.mount.errorTitle'),
        description: error instanceof Error ? error.message : t('customerPortal.mount.errorDescription'),
        variant: 'destructive',
      });
    } finally {
      setIsMounting(false);
    }
  };

  const isLoading = isLoadingFunnels || scan.loading;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('customerPortal.mount.title')}</CardTitle>
        <CardDescription>{t('customerPortal.mount.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : isFunnelsError ? (
          <p className="text-muted-foreground text-sm">{t('customerPortal.mount.funnelsError')}</p>
        ) : scan.funnels.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('customerPortal.mount.noFunnels')}</p>
        ) : (
          <>
            {mounted.length > 0 && (
              <section className="space-y-3">
                <p className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
                  {t('customerPortal.mount.liveHeading')}
                </p>
                {mounted.map((funnel) => (
                  <div
                    key={funnel.id}
                    className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-0">
                      <p className="truncate font-medium">{funnel.name}</p>
                      {funnel.url ? (
                        <p className="text-muted-foreground truncate text-sm">{funnel.url}</p>
                      ) : (
                        <p className="text-muted-foreground text-sm">
                          {t('customerPortal.mount.urlPending')}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-2">
                      {funnel.isStagingOnly && (
                        <Badge variant="gray">{t('customerPortal.mount.stagingBadge')}</Badge>
                      )}
                      {funnel.url && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-2"
                            onClick={() => copyUrl(funnel.url!)}
                          >
                            {copiedUrl === funnel.url ? (
                              <Check className="h-4 w-4" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                            {t('customerPortal.mount.copyUrl')}
                          </Button>
                          <Button variant="outline" size="sm" className="gap-2" asChild>
                            <a href={funnel.url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="h-4 w-4" />
                              {t('customerPortal.mount.openUrl')}
                            </a>
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </section>
            )}

            {missing.length > 0 && (
              <section className="space-y-3">
                <p className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
                  {t('customerPortal.mount.addHeading')}
                </p>
                {missing.map((funnel) => (
                  <label
                    key={funnel.id}
                    className="flex cursor-pointer items-center gap-3 rounded-md border p-3"
                  >
                    <Checkbox
                      checked={selectedIds.has(funnel.id)}
                      onCheckedChange={(checked) => toggleSelected(funnel.id, checked === true)}
                    />
                    <span className="truncate font-medium">{funnel.name}</span>
                  </label>
                ))}
                <Button
                  onClick={addPortalToSelectedFunnels}
                  disabled={selectedCount === 0 || isMounting}
                  className="gap-2"
                >
                  {isMounting && <Loader2 className="h-4 w-4 animate-spin" />}
                  {t('customerPortal.mount.addAction', { count: selectedCount })}
                </Button>
              </section>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
